import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { MapPin, Mail, Phone, CheckCircle, Send, Loader2 } from 'lucide-react';
import { useSectionContent } from '../hooks/useSiteContent';
import { leadService } from '../services/api';

// Default contact content (BlueLock Freights brand; CMS overrides at runtime)
const CONTACT_DEFAULTS = {
  contact_hero_title: "Contact BlueLock Freights",
  contact_hero_subtitle: "General inquiries, recruiting, dispatch, and compliance.",
  contact_recruiting_email: "recruiting@bluelockfreights.com",
  contact_general_email: "info@bluelockfreights.com",
  contact_dispatch_email: "dispatch@bluelockfreights.com",
  contact_compliance_email: "compliance@bluelockfreights.com",
  contact_phone: "616-438-6969",
  contact_after_hours_phone: "616-292-9452",
  contact_address: "4370 Chicago Drive SW, Grandville, MI 49418",
  contact_location: "Grandville, Michigan",
  contact_website: "https://bluelockfreights.com"
};

const ContactPage = () => {
  const { content } = useSectionContent('contact_info', CONTACT_DEFAULTS);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Slice 1 — Public Lead Capture. Persist to the leads backend when the
    // capability is active. When the feature flag is OFF (403) or on any transient
    // error, degrade gracefully to the prior confirmation UX (no regression).
    try {
      await leadService.submitPublicLead({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message || null,
        service_type: 'general',
        source: 'contact_page',
      });
    } catch (err) {
      // Flag OFF / network error: silently fall back to prior behavior.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <PublicNavbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {content.contact_hero_title}
          </h1>
          <p className="text-lg text-blue-100">
            {content.contact_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Driver Recruiting</h3>
                    <a 
                      href={`mailto:${content.contact_recruiting_email}`}
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                      data-testid="contact-recruiting-email"
                    >
                      {content.contact_recruiting_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Dispatch</h3>
                    <a 
                      href={`mailto:${content.contact_dispatch_email}`}
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                      data-testid="contact-dispatch-email"
                    >
                      {content.contact_dispatch_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Compliance</h3>
                    <a 
                      href={`mailto:${content.contact_compliance_email}`}
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                      data-testid="contact-compliance-email"
                    >
                      {content.contact_compliance_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">General Inquiries</h3>
                    <a 
                      href={`mailto:${content.contact_general_email}`}
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                      data-testid="contact-general-email"
                    >
                      {content.contact_general_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call / Text</h3>
                    <a 
                      href={`tel:${content.contact_phone.replace(/[^0-9+]/g, '')}`}
                      className="text-blue-900 hover:text-blue-700 transition-colors block"
                      data-testid="contact-phone-link"
                    >
                      {content.contact_phone}
                    </a>
                    <a 
                      href={`tel:${content.contact_after_hours_phone.replace(/[^0-9+]/g, '')}`}
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors block mt-1"
                      data-testid="contact-after-hours-phone"
                    >
                      After Hours: {content.contact_after_hours_phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Headquarters</h3>
                    <p className="text-gray-700" data-testid="contact-address">{content.contact_address}</p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <Link 
                      to="/drivers" 
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                    >
                      View Driver Opportunities →
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/apply" 
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                    >
                      Submit a Driver Application →
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/faq" 
                      className="text-blue-900 hover:text-blue-700 transition-colors"
                    >
                      Read the FAQ →
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Send a Message</h2>
              
              {submitted ? (
                <div className="bg-green-50 rounded-2xl p-8 border border-green-200 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank you.</h3>
                  <p className="text-gray-600">Your message has been received.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        data-testid="contact-full-name"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-message"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-900 text-white rounded-lg font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
